<template>
  <div>
    <div class="wg-cp-head">
      <h2>How each article is doing</h2>
      <div class="wg-fpills">
        <button
          v-for="pill in pills"
          :key="pill.id"
          class="wg-fpill"
          :class="{ active: filter === pill.id }"
          @click="filter = pill.id"
        >
          {{ pill.label }} <span class="ct">{{ pill.count }}</span>
        </button>
      </div>
    </div>

    <div class="wg-wtable">
      <div class="wg-wtrow simple wg-wthead">
        <div class="c">Article</div>
        <div class="c">How it's doing</div>
        <div class="c">
          <v-tooltip top color="transparent" max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">People saw you</span>
            </template>
            <div class="white-bg-tooltip small-tooltip">The number of times your page appeared in Google's results, whether or not anyone clicked.</div>
          </v-tooltip>
        </div>
        <div class="c">
          <v-tooltip top color="transparent" max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">People clicked</span>
            </template>
            <div class="white-bg-tooltip small-tooltip">The number of times someone actually clicked through to your site from Google.</div>
          </v-tooltip>
        </div>
        <div class="c">
          <v-tooltip top color="transparent" max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">Searches you<br />show up for</span>
            </template>
            <div class="white-bg-tooltip small-tooltip">The different searches your pages appear for in Google.</div>
          </v-tooltip>
        </div>
        <div class="c">
          <v-tooltip top color="transparent" max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">Where on Google</span>
            </template>
            <div class="white-bg-tooltip small-tooltip">Which page of Google's results your article appears on. Page 1 is the top 10 results, where most clicks happen.</div>
          </v-tooltip>
        </div>
      </div>

      <div v-if="visibleGroups.length === 0 && visibleWaiting.length === 0" class="wg-wtrow wg-empty-row">
        <div class="wg-cempty">
          <i class="material-icons">search</i>
          <div class="t">No content in this view</div>
          <div class="s">
            Nothing matches this filter for the selected {{ periodWord }}. Try another filter, or
            check All Content.
          </div>
        </div>
      </div>

      <template v-for="group in visibleGroups">
        <div
          :key="'g' + (group.siloReportId || group.name)"
          class="wg-wtrow simple wg-grp"
          :class="{ open: isOpen(group) }"
          @click="toggle(group)"
        >
          <div class="wg-c1">
            <i class="material-icons gcaret" :class="{ open: isOpen(group) }">expand_more</i>
            <div>
              <div class="tt">{{ group.name }}</div>
              <div class="ss">{{ group.articles.length }} {{ group.articles.length === 1 ? 'article' : 'articles' }}</div>
            </div>
          </div>
          <div class="c">
            <v-tooltip top color="transparent" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <span class="wg-status" :class="statusClass(groupStatus(group))" v-bind="attrs" v-on="on">{{ statusLabel(groupStatus(group)) }}</span>
              </template>
              <div class="white-bg-tooltip small-tooltip">{{ statusTip(groupStatus(group)) }}</div>
            </v-tooltip>
          </div>
          <div class="c">
            <div>{{ fmt(group.impressions) }}</div>
            <span v-if="group.prevImpressions != null" class="nd">was {{ fmt(group.prevImpressions) }}</span>
          </div>
          <div class="c">
            <div>{{ fmt(group.clicks) }}</div>
            <span v-if="group.prevClicks != null" class="nd">was {{ fmt(group.prevClicks) }}</span>
          </div>
          <div class="c">
            <div>{{ fmt(group.queriesCount) }}</div>
            <span v-if="group.prevQueriesCount != null" class="nd">was {{ fmt(group.prevQueriesCount) }}</span>
          </div>
          <div class="c">
            <span v-if="pagePosition(group)" class="wg-pagepill" :class="pageClass(pagePosition(group))">
              {{ pageLabel(pagePosition(group)) }}
            </span>
            <span v-else class="wg-dash">–</span>
            <span v-if="pageMoved(group)" class="pmove">was {{ pageLabel(group.prevAvgPosition) }}</span>
          </div>
        </div>

        <template v-if="isOpen(group)">
          <div
            v-for="article in group.articles"
            :key="'a' + article.trackedUrlId"
            class="wg-wtrow simple wg-child"
          >
            <div class="wg-c1">
              <div>
                <div class="tt">{{ article.title }}</div>
                <a class="url" :href="article.url" target="_blank" rel="noopener noreferrer">{{ article.url }}</a>
              </div>
            </div>
            <div class="c">
              <v-tooltip top color="transparent" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <span class="wg-status" :class="statusClass(article.status)" v-bind="attrs" v-on="on">{{ statusLabel(article.status) }}</span>
                </template>
                <div class="white-bg-tooltip small-tooltip">{{ statusTip(article.status, article) }}</div>
              </v-tooltip>
              <span v-if="settleSub(article)" class="pmove">{{ settleSub(article) }}</span>
            </div>
            <div class="c">
              <div>{{ fmt(article.impressions) }}</div>
              <span v-if="article.prevImpressions != null" class="nd">was {{ fmt(article.prevImpressions) }}</span>
            </div>
            <div class="c">
              <div>{{ fmt(article.clicks) }}</div>
              <span v-if="article.prevClicks != null" class="nd">was {{ fmt(article.prevClicks) }}</span>
            </div>
            <div class="c">
              <div>{{ fmt(article.queryCount) }}</div>
              <span v-if="article.prevQueryCount != null" class="nd">was {{ fmt(article.prevQueryCount) }}</span>
            </div>
            <div class="c">
              <span v-if="pagePosition(article)" class="wg-pagepill" :class="pageClass(pagePosition(article))">
                {{ pageLabel(pagePosition(article)) }}
              </span>
              <span v-else class="wg-dash">–</span>
              <span v-if="pageMoved(article)" class="pmove">was {{ pageLabel(article.prevAvgPosition) }}</span>
            </div>
          </div>
        </template>
      </template>

      <!-- delivered articles waiting on a URL: own filter chip, not counted in totals -->
      <div
        v-for="row in visibleWaiting"
        :key="'w' + row.siloKeywordId"
        class="wg-wtrow simple"
      >
        <div class="wg-c1">
          <div>
            <div class="tt">{{ row.keyword }}</div>
            <div class="ss">We need the live link</div>
          </div>
        </div>
        <div class="c">
          <v-tooltip top color="transparent" max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <span class="wg-status c-grey" v-bind="attrs" v-on="on">Waiting on URL</span>
            </template>
            <div class="white-bg-tooltip small-tooltip">{{ statusTip('waiting_on_url') }}</div>
          </v-tooltip>
        </div>
        <div class="c wg-dash">–</div>
        <div class="c wg-dash">–</div>
        <div class="c wg-dash">–</div>
        <div class="c wg-dash">–</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WinsContentTable',
  props: {
    groups: {
      type: Array,
      default: () => [],
    },
    waitingOnUrl: {
      type: Array,
      default: () => [],
    },
    periodWord: {
      type: String,
      default: 'quarter',
    },
  },
  data () {
    return {
      filter: 'all',
      openGroups: {},
    }
  },
  computed: {
    pills () {
      const countOf = (match) => this.groups
        .filter(match)
        .reduce((total, g) => total + g.articles.length, 0)
      return [
        { id: 'all', label: 'All Content', count: countOf(() => true) },
        { id: 'content_plan', label: 'Content plans', count: countOf((g) => g.source === 'content_plan') },
        { id: 'content_only', label: 'Content only', count: countOf((g) => g.source === 'content_only') },
        { id: 'waiting', label: 'Waiting on URL', count: this.waitingOnUrl.length },
      ]
    },
    visibleGroups () {
      if (this.filter === 'waiting') return []
      if (this.filter === 'all') return this.groups
      return this.groups.filter((g) => g.source === this.filter)
    },
    visibleWaiting () {
      return this.filter === 'waiting' ? this.waitingOnUrl : []
    },
  },
  methods: {
    fmt (value) {
      return (value || 0).toLocaleString('en-US')
    },
    groupKey (group) {
      return String(group.siloReportId || group.name)
    },
    isOpen (group) {
      return Boolean(this.openGroups[this.groupKey(group)])
    },
    toggle (group) {
      this.$set(this.openGroups, this.groupKey(group), !this.isOpen(group))
    },
    groupStatus (group) {
      const statuses = new Set(group.articles.map((a) => a.status))
      for (const status of ['lost_signal', 'climbing', 'early_days', 'needs_nudge', 'steady']) {
        if (statuses.has(status)) return status
      }
      return 'early_days'
    },
    statusLabel (status) {
      return {
        climbing: 'Climbing',
        early_days: 'Early days',
        needs_nudge: 'Needs a nudge',
        steady: 'Steady',
        waiting_on_url: 'Waiting on URL',
        lost_signal: 'Lost signal',
      }[status] || status
    },
    statusClass (status) {
      return {
        climbing: 'c-green',
        early_days: 'c-blue',
        needs_nudge: 'c-amber',
        steady: 'c-grey',
        waiting_on_url: 'c-grey',
        lost_signal: 'c-red',
      }[status] || 'c-grey'
    },
    /** 90-day settling window, counted from the publish date. */
    settleDate (row) {
      if (!row || !row.since) return null
      const [y, m, d] = row.since.split('-').map(Number)
      const settle = new Date(y, m - 1, d)
      settle.setDate(settle.getDate() + 90)
      return settle
    },
    fmtSettle (date) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    settleSub (row) {
      if (!row || row.status !== 'early_days') return ''
      const settle = this.settleDate(row)
      if (!settle || settle <= new Date()) return ''
      return `settles ~${this.fmtSettle(settle)}`
    },
    statusTip (status, row) {
      if (status === 'early_days' && row) {
        const settle = this.settleDate(row)
        if (settle && settle > new Date()) {
          return `Google is still evaluating this page. Numbers become reliable around ${this.fmtSettle(settle)}, 90 days after publishing (from Search Console's first record of the page).`
        }
      }
      if (status === 'steady' && row) {
        const settle = this.settleDate(row)
        if (settle) {
          return `This page passed its 90-day settling period on ${this.fmtSettle(settle)} - its numbers are reliable. We keep monitoring.`
        }
      }
      return {
        climbing: "In Google's top 10. We keep monitoring to hold the spot.",
        early_days: 'Google is still evaluating this page. Visibility typically settles in 8-12 weeks.',
        needs_nudge: 'Close to page 1. Usually links or a content refresh get it over the line.',
        steady: 'Past its settling period - numbers are reliable. We keep monitoring.',
        waiting_on_url: 'Add the live link in Settings so we can start measuring.',
        lost_signal: 'This URL stopped returning data in Search Console. If the address changed, update it in Settings; its history is kept.',
      }[status] || ''
    },
    /** "Where on Google" reflects the page's average position (matches the
     * GSC UI); older snapshots without it fall back to best position. */
    pagePosition (row) {
      return row.avgPosition || row.bestPosition
    },
    /** "was Page N" shows only when the previous period's bucket differs. */
    pageMoved (row) {
      const now = this.pagePosition(row)
      return Boolean(row.prevAvgPosition && now &&
        this.pageLabel(row.prevAvgPosition) !== this.pageLabel(now))
    },
    pageLabel (position) {
      if (position <= 10) return 'Page 1'
      if (position <= 20) return 'Page 2'
      return 'Page 3+'
    },
    pageClass (position) {
      if (position <= 10) return 'p1'
      if (position <= 20) return 'p2'
      return 'p3'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../Settings/wg-tokens.scss';

/* previous-period value under the number / pill (design_updated .nd/.pmove) */
.nd {
  display: block;
  font-size: 0.72rem;
  font-weight: 500;
  color: $pop-ink-faint;
  margin-top: 2px;
}

.pmove {
  display: block;
  font-size: 0.72rem;
  font-weight: 500;
  color: $pop-ink-faint;
  margin-top: 4px;
}

/* content performance */
.wg-cp-head {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;

  h2 {
    font-size: 1.1rem;
    font-weight: 700;
    color: $pop-teal;
    margin: 0;
  }
}

.wg-fpills {
  display: flex;
  gap: 8px;
}

.wg-fpill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 5px 6px 5px 14px;
  cursor: pointer;
  border: 1px solid $pop-border;
  background: #fff;
  color: $pop-teal;
  transition: background 0.15s, border-color 0.15s;

  .ct {
    font-size: 0.7rem;
    font-weight: 700;
    min-width: 20px;
    height: 20px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: $pop-box-bg;
    color: $pop-ink-muted;
    padding: 0 4px;
  }

  &.active {
    background: $pop-accent-active;
    border-color: $pop-accent;
    color: $pop-accent;

    .ct {
      background: $pop-accent;
      color: #fff;
    }
  }
}

/* table */
.wg-wtable {
  background: #fff;
  border: 1px solid $wg-panel-line;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 30px;
}

.wg-wtrow {
  align-items: center;
  padding: 0 20px;
  border-bottom: 1px solid $wg-hairline;
  min-height: 58px;

  &.simple {
    display: grid;
    grid-template-columns: minmax(220px, 2.2fr) 1.2fr 1fr 1fr 1.1fr 1.1fr;
  }

  &:last-child {
    border-bottom: none;
  }

  .c {
    font-size: 0.85rem;
    color: $pop-teal;
    font-weight: 600;
    white-space: nowrap;
  }

  &.wg-empty-row {
    display: block;
    padding: 0;
  }
}

.wg-wthead {
  background: $wg-lavender;
  min-height: 46px;

  /* the mock's cascade resolves header cells to the dark row style
     (teal, row-size) - keep the label color in step with the body */
  .c {
    font-size: 0.85rem;
    font-weight: 600;
    color: $pop-teal;
    white-space: normal;
    line-height: 1.3;
  }
}

.wg-grp {
  cursor: pointer;
  background: #fff;

  &:hover,
  &.open {
    background: $wg-lavender;
  }
}

.wg-c1 {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  /* the text block is a flex item: with the default min-width auto it took the width of its
     nowrap title and ran under the status pill (tester, 2026-10-21); let it shrink so .tt wraps */
  > div {
    min-width: 0;
    flex: 1 1 auto;
  }

  .gcaret {
    color: $pop-ink-muted;
    transition: transform 0.15s;
    flex: none;
    font-size: 1.35rem;

    &.open {
      transform: rotate(180deg);
    }
  }

  .tt {
    font-size: 0.9rem;
    font-weight: 700;
    color: $pop-teal;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }

  .ss {
    font-size: 0.75rem;
    color: $pop-ink-muted;
    margin-top: 2px;
    white-space: nowrap;
  }
}

.wg-child {
  .wg-c1 {
    padding-left: 30px;
  }

  .tt {
    font-size: 0.85rem;
  }

  .url {
    font-size: 0.75rem;
    color: $pop-link;
    margin-top: 3px;
    text-decoration: none;
    display: block;
    max-width: 215px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }
}

/* pills */
.wg-status {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 11px;
  border-radius: 30px;
  white-space: nowrap;

  &.c-green {
    background: $pop-success-bg;
    color: $pop-success;
  }

  &.c-blue {
    background: $pop-info-bg;
    color: $pop-link;
  }

  &.c-red {
    background: $pop-danger-bg;
    color: $pop-danger;
  }

  &.c-amber {
    background: $pop-warning-bg;
    color: #8a5a10;
  }

  &.c-grey {
    background: $pop-box-bg;
    color: $pop-ink-muted;
  }
}

.wg-pagepill {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 4px 11px;
  border-radius: 6px;
  white-space: nowrap;

  &.p1 {
    background: $pop-success-bg;
    color: $pop-success;
  }

  &.p2 {
    background: #fff3e2;
    color: #9a5b00;
  }

  &.p3 {
    background: $pop-box-bg;
    color: $pop-ink-muted;
  }
}

.wg-dash {
  color: $pop-ink-faint;
}

/* compact in-card empty state */
.wg-cempty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 34px 20px;
  text-align: center;

  .material-icons {
    font-size: 34px;
    color: $wg-hairline;
  }

  .t {
    font-size: 1.05rem;
    font-weight: 800;
    color: $pop-ink-faint;
  }

  .s {
    font-size: 0.85rem;
    line-height: 1.5;
    color: $pop-ink-muted;
    max-width: 420px;
  }
}
</style>
